import React, { useMemo, useState } from 'react';
import { BookOpenText, ClipboardCheck, FileText, PencilLine } from 'lucide-react';
import ListItemCard from '../../../components/common/ListItemCard';
import { Card } from '../../../components/ui/Card';
import { ClassLesson } from '../types/classLessons';
import ClassLessonSummaryCards from './ClassLessonSummaryCards';

interface ClassLessonsPanelProps {
  studentsCount: number;
  visitorsCount: number;
  attendanceAverage: string;
  lessonsCompleted: number;
  recurringAbsenteesCount: number;
  lessons: Array<
    ClassLesson & {
      attendanceSummaryLabel: string;
      displayDate: string;
      displayDateKind: 'planned' | 'held' | null;
      hasAttendance: boolean;
      isConfigured: boolean;
    }
  >;
  onOpenLessonDetails: (lessonId: string) => void;
  onOpenLessonReport: (lessonId: string) => void;
}

const formatDateLabel = (value: string) => {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(`${value}T00:00:00`));
};

const ClassLessonsPanel: React.FC<ClassLessonsPanelProps> = ({
  studentsCount,
  visitorsCount,
  attendanceAverage,
  lessonsCompleted,
  recurringAbsenteesCount,
  lessons,
  onOpenLessonDetails,
  onOpenLessonReport,
}) => {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const metricLabelStyles = useMemo(
    () => ({
      withDate: 'bg-slate-100 text-slate-700',
    }),
    []
  );

  return (
    <section className="space-y-4">
      <Card className="border-slate-200 bg-white p-4 shadow-[0_18px_40px_-24px_rgba(15,23,42,0.3)]">
        <ClassLessonSummaryCards
          studentsCount={studentsCount}
          visitorsCount={visitorsCount}
          attendanceAverage={attendanceAverage}
          lessonsCompleted={lessonsCompleted}
          recurringAbsenteesCount={recurringAbsenteesCount}
        />
      </Card>

      {lessons.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
          Nenhuma lição encontrada para o segmento desta turma no trimestre selecionado.
        </div>
      ) : (
        <div className="space-y-4">
          {lessons.map((lesson) => (
          <ListItemCard
            key={lesson.id}
            title={lesson.title || `Lição ${lesson.lessonNumber}`}
            onClick={() => onOpenLessonDetails(lesson.id)}
            leadingIcon={BookOpenText}
            leadingTone="from-amber-100 to-orange-100 text-amber-700"
            badges={[
              {
                label: `Lição ${lesson.lessonNumber}`,
                className: 'bg-slate-100 text-slate-700',
              },
              ...(lesson.displayDate
                ? [
                    {
                      label: `${lesson.displayDateKind === 'held' ? 'Realizada' : 'Prevista'}: ${formatDateLabel(
                        lesson.displayDate
                      )}`,
                      className: metricLabelStyles.withDate,
                    },
                  ]
                : []),
              {
                label: lesson.hasAttendance ? 'Frequência lançada' : 'Sem frequência',
                className: lesson.hasAttendance ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500',
              },
            ]}
            menuOpen={openMenuId === lesson.id}
            onToggleMenu={() => setOpenMenuId(openMenuId === lesson.id ? null : lesson.id)}
            actions={[
              {
                label: 'Editar lição',
                icon: PencilLine,
                tone: 'bg-sky-50 text-sky-600',
                onClick: () => {
                  setOpenMenuId(null);
                  onOpenLessonDetails(lesson.id);
                },
              },
              {
                label: lesson.hasAttendance ? 'Ajustar frequência' : 'Registrar frequência',
                icon: ClipboardCheck,
                tone: 'bg-emerald-50 text-emerald-700',
                onClick: () => {
                  setOpenMenuId(null);
                  onOpenLessonDetails(lesson.id);
                },
              },
              {
                label: 'Ver relatório',
                icon: FileText,
                tone: 'bg-sky-50 text-sky-700',
                onClick: () => {
                  setOpenMenuId(null);
                  onOpenLessonReport(lesson.id);
                },
              },
            ]}
            metricLeft={
              undefined
            }
          />
          ))}
        </div>
      )}
    </section>
  );
};

export default ClassLessonsPanel;
